import React from "react";
import { toast } from "sonner";
import { MusicNotes, SpeakerHigh, Waveform } from "@phosphor-icons/react";
import { useAudio } from "../context/AudioContext";

const TOGGLES = [
  ["music_enabled", "Enable adaptive music", "Turns the Expedition Pulse soundtrack on for your account."],
  ["mute_all", "Mute all audio", "Silences music and effects everywhere."],
  ["calm_mode", "Calm mode", "Softer, lower-intensity scoring throughout."],
  ["reduced_intensity", "Reduced intensity", "Tones down louder danger and result moments."],
  ["dynamic_tension_enabled", "Dynamic tension", "Let the music rise and fall with the expedition."],
  ["beat_sync_enabled", "Synchronize decorative visuals with music", "Decorative effects only. This setting does not affect gameplay or scoring."],
  ["reduced_bass", "Reduced bass", "Lower low-frequency energy."],
  ["reduced_high_freq", "Reduced high frequencies", "Soften bright, sharp tones."],
  ["mono_mix", "Mono mix", "Combine stereo channels into one."],
];

const CREW_TOGGLES = [
  ["crew_motifs_enabled", "Crew motifs", "Short musical themes for your crew during key moments."],
  ["reduce_ceremonial_motifs", "Reduce ceremonial motifs", "Softer, shorter themes for big crew moments."],
  ["disable_injury_motifs", "Disable injury motifs", "Skip the altered theme when a crew member is hurt."],
];

const Toggle = ({ id, label, hint, checked, disabled, onChange }) => (
  <label className="flex cursor-pointer items-start justify-between gap-3 rounded-sm px-2 py-2 hover:bg-ff-bg/50" data-testid={`audio-${id}`}>
    <span>
      <span className="block text-sm text-ff-parchment">{label}</span>
      <span className="block text-[11px] text-ff-muted">{hint}</span>
    </span>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange}
      className="mt-1 h-4 w-8 cursor-pointer accent-ff-gold disabled:cursor-not-allowed disabled:opacity-50" />
  </label>
);

export default function AudioSettingsPanel() {
  const audio = useAudio();
  if (!audio) return null;
  const { flags, settings, updateSetting } = audio;
  const featureOn = !!flags?.ADAPTIVE_AUDIO_ENABLED;

  const set = async (patch) => {
    try { await updateSetting(patch); toast.success("Audio preferences saved"); }
    catch (e) { toast.error("Couldn't save audio preferences"); }
  };

  return (
    <section className="ff-panel rounded-md p-5" data-testid="settings-audio">
      <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-semibold">
        <MusicNotes size={20} weight="duotone" className="text-ff-brass" /> The Expedition Pulse — Audio
      </h2>

      {!featureOn && (
        <div className="mb-3 rounded-sm border border-ff-gold/30 bg-ff-gold/5 p-3 text-xs text-ff-gold" data-testid="audio-preview-banner">
          <b>Coming soon.</b> Adaptive audio is in preview and not yet playing. Your preferences below are saved now and
          will apply automatically the moment the soundtrack launches — no sound is produced until then.
        </div>
      )}
      {featureOn && (
        <p className="mb-3 text-xs text-ff-muted">Scoring is identical regardless of audio settings — hearing is never required to play or win.</p>
      )}

      <div className="space-y-1">
        {TOGGLES.map(([id, label, hint]) => (
          <Toggle key={id} id={id} label={label} hint={hint}
            checked={!!settings?.[id]} disabled={false}
            onChange={() => set({ [id]: !settings?.[id] })} />
        ))}
      </div>

      <div className="mt-4 space-y-4 border-t border-ff-brass/15 pt-4">
        <div data-testid="audio-music-volume">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-ff-parchment"><SpeakerHigh size={15} weight="duotone" className="text-ff-brass" /> Music volume</span>
            <span className="text-ff-muted">{Math.round((settings?.music_volume ?? 0.6) * 100)}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={settings?.music_volume ?? 0.6}
            onChange={(e) => set({ music_volume: parseFloat(e.target.value) })}
            className="w-full cursor-pointer accent-ff-gold" data-testid="audio-music-volume-slider" />
        </div>
        <div data-testid="audio-effects-volume">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-ff-parchment"><Waveform size={15} weight="duotone" className="text-ff-brass" /> Effects volume</span>
            <span className="text-ff-muted">{Math.round((settings?.effects_volume ?? 0.7) * 100)}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={settings?.effects_volume ?? 0.7}
            onChange={(e) => set({ effects_volume: parseFloat(e.target.value) })}
            className="w-full cursor-pointer accent-ff-gold" data-testid="audio-effects-volume-slider" />
        </div>
        <div data-testid="audio-preferred-intensity">
          <div className="mb-1 text-sm text-ff-parchment">Preferred intensity</div>
          <div className="flex gap-1.5">
            {["standard", "reduced", "calm"].map((v) => (
              <button key={v} onClick={() => set({ preferred_intensity: v })}
                data-testid={`audio-intensity-${v}`}
                className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${settings?.preferred_intensity === v ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-1 border-t border-ff-brass/15 pt-4" data-testid="audio-crew-motifs">
        <h3 className="mb-1 text-sm font-semibold text-ff-parchment">Crew motifs</h3>
        <p className="mb-2 text-[11px] text-ff-muted">Crew motifs are decorative musical themes. They do not affect gameplay, scoring, or crew performance.</p>
        {CREW_TOGGLES.map(([id, label, hint]) => (
          <Toggle key={id} id={id} label={label} hint={hint}
            checked={!!settings?.[id]} disabled={false}
            onChange={() => set({ [id]: !settings?.[id] })} />
        ))}
        <div data-testid="audio-crew-motif-volume">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-ff-parchment"><SpeakerHigh size={15} weight="duotone" className="text-ff-brass" /> Crew motif volume</span>
            <span className="text-ff-muted">{Math.round((settings?.crew_motif_volume ?? 0.6) * 100)}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={settings?.crew_motif_volume ?? 0.6}
            onChange={(e) => set({ crew_motif_volume: parseFloat(e.target.value) })}
            className="w-full cursor-pointer accent-ff-gold" data-testid="audio-crew-motif-volume-slider" />
        </div>
      </div>
    </section>
  );
}
